import UploadIcon from '@ant-design/icons/UploadOutlined';
import {
  Box,
  // LinearProgress,
  Skeleton,
  Tab, Tabs,
  Typography,
  // Typography,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { AsyncState, ClientFile, Questionnaire } from 'src/models';
import { resetDocsToUpload, resetUploadedDocs, setActiveTitleId, setTabIndex } from 'src/store/questionnaire/questionnaireSlice';
import NoQuestionnaire from './no_question';

// Constants
const PURPLE_THEME = '#663399';

// Styled Components
const CustomTab = styled(Tab)(({ theme }) => ({
  padding: '1.4rem',
  margin: '5px',
  alignItems: 'flex-start',
  textAlign: 'left',
  color: 'GrayText',
  fontSize: '16px',
  '& .MuiTab-wrapper': {        // Add this styling for the wrapper
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
  },
  '&.Mui-selected': {
    color: 'white',
    padding: '1.6rem 1.4rem',
    backgroundColor: PURPLE_THEME,
    textAlign: 'left',
    fontWeight: 'bold',
  },
  '&:hover': {
    color: PURPLE_THEME,
  },
}));


const LoadingTabs = () => (
  <Box padding={2}>
    {[...Array(4)].map((_, i) => (
      <Box key={i} marginBottom={1}>
        <Skeleton variant="text" height={38} animation="wave" />
      </Box>
    ))}
  </Box>
);

const a11yProps = (index: number) => ({
  id: `vertical-tab-${index}`,
  'aria-controls': `vertical-tabpanel-${index}`,
});

const SidebarContent: React.FC<{
  headings: string[] | null;
  required_docs: string[] | null;
  uploaded_docs: ClientFile[]
  closeSidebar: () => void
  status: AsyncState | null;
  questionnaires: Questionnaire[]
  action?: string
  tabIndex: number;
}> = ({ headings, status, tabIndex, closeSidebar, action, required_docs, uploaded_docs }) => {
  const dispatch = useAppDispatch()
  const { docs_to_upload, kyc_section_titles } = useAppSelector((state) => state.applications)
  const [selectedDoc, setSelectedDoc] = useState('')
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (headings == null) return;
    const section = kyc_section_titles.find(section => section.title === headings[tabIndex]);
    dispatch(setActiveTitleId(section?.id))
    // eslint-disable-next-line
  }, [tabIndex, headings])

  const handleClick = (doc: string) => {
    setSelectedDoc(doc);
    fileInputRef.current?.click();
  };

  return (
    <Box height="100%" display={'flex'} flexDirection={'column'}>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={tabIndex}
        onChange={(_, newValue) => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth' // Adds smooth scrolling
          });
          if (isSmallScreen) {
            closeSidebar();
          }
          dispatch(setTabIndex(newValue));
        }}
        aria-label="Questionnaire sections"
        sx={{ flexGrow: 1, width: '100%', paddingInline: '10px' }}
      >
        {headings === null && status === 'pending' && action !== 'submit' ? (
          <LoadingTabs />
        ) : (headings ?? []).length > 0 ? (
          headings?.map((heading, i) => (
            <CustomTab key={i} label={heading} {...a11yProps(i)} />
          ))
        ) : (
          <NoQuestionnaire />
        )}
      </Tabs>
      <Box sx={{ m: 2, border: '0px solid red' }} overflow={'hidden'}>
        {(required_docs ?? []).length > 0 &&
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>Required Documents ({required_docs?.length})</Typography>
        }
        <Box maxHeight={'350px'} mb={2} overflow={'auto'}>
          {/* Show all uploaded documents first */}
          {uploaded_docs.map((doc, i) => (
            <Box
              key={`uploaded-${i}`}
              onClick={() => handleClick(doc.file_name)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2,
                border: '1px dashed rgba(196, 196, 196, 0.71)',
                borderRadius: '6px',
                padding: '10px',
                cursor: 'pointer'
              }}
            >
              <Box position={'relative'} width={'100%'}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    const restOfFiles = uploaded_docs.filter((d) => d.file_name !== doc.file_name)
                    dispatch(resetUploadedDocs(restOfFiles))
                  }}
                  position={'absolute'}
                  top={-10}
                  p={0.2}
                  borderRadius={0.5}
                  right={-10}
                  bgcolor={'red'}
                  color={'white'}
                >
                  <Typography variant='subtitle2' fontWeight={'bold'} fontSize={'0.8rem'}>Remove</Typography>
                </Box>
                <Typography
                  variant="body2"
                  ml={1}
                  fontWeight={'500'}
                  color='rgba(196, 196, 196, 0.71)'
                >
                  {doc.file_name}
                </Typography>
              </Box>
            </Box>
          ))}

          {/* Show remaining required documents that haven't been uploaded yet */}
          {required_docs?.filter(req => {
            // Check if this required doc is not in the uploaded_docs
            return !uploaded_docs.some(doc => doc.file_name === req);
          }).map((doc, i) => {
            const isDocSelected = Object.keys(docs_to_upload).includes(doc);

            return (
              <Box
                key={`required-${i}`}
                onClick={() => handleClick(doc)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 2,
                  border: '1px dashed rgba(196, 196, 196, 0.71)',
                  borderRadius: '6px',
                  padding: '10px',
                  cursor: 'pointer'
                }}
              >
                {isDocSelected ?
                  <Box position={'relative'} width={'100%'}>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (Object.keys(docs_to_upload).includes(doc)) {
                          const { [doc]: _, ...rest } = docs_to_upload;
                          dispatch(resetDocsToUpload(rest))
                        }
                      }}
                      position={'absolute'}
                      top={-10}
                      p={0.2}
                      borderRadius={0.5}
                      right={-10}
                      bgcolor={'red'}
                      color={'white'}
                    >
                      <Typography variant='subtitle2' fontWeight={'bold'} fontSize={'0.8rem'}>Remove</Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      ml={1}
                      fontWeight={'500'}
                      textAlign={'left'}
                      color='rgba(196, 196, 196, 0.71)'
                    >
                      {docs_to_upload[doc].name}
                    </Typography>
                  </Box>
                  :
                  <>
                    <UploadIcon />
                    <Typography
                      variant="body2"
                      ml={1}
                      fontWeight={'500'}
                      color='rgba(196, 196, 196, 0.71)'
                      textAlign={'center'}
                    >
                      Upload {doc}
                    </Typography>
                  </>
                }
              </Box>
            );
          })}

          <input
            type="file"
            ref={fileInputRef}
            multiple={false}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files?.length) {
                const restOfFiles = uploaded_docs.filter((d) => d.file_name !== selectedDoc)
                if (restOfFiles.length > 0) {
                  dispatch(resetUploadedDocs(restOfFiles))
                }
                const docs = { ...docs_to_upload };
                docs[selectedDoc] = e.target.files[0]
                dispatch(resetDocsToUpload(docs))
              }
            }}
          />
        </Box>
      </Box>
    </Box >
  )
};

export default SidebarContent